import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Organization, OrganizationType, RegulationPreference, Currency, DEFAULT_CURRENCY } from '../models/Organization';
import { CURRENCY_META, SUPPORTED_CURRENCIES } from '../utils/currency';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PhoneInput } from './ui/PhoneInput';
import { Timestamp } from '../lib/timestamp';
import { logger } from '../utils/logger';
import { useLanguage } from '../contexts/LanguageContext';


interface OrganizationSetupFormProps {
  ownerUid: string;
  onSuccess: (organization: Organization) => void;
  onCancel: () => void;
  isOpen: boolean;
  guidance?: React.ReactNode;
  stepper?: React.ReactNode;
  needHelp?: React.ReactNode;
}

interface FormData {
  name: string;
  type: OrganizationType;
  regulationPreference: RegulationPreference;
  currency: Currency;
  baseAirport: {
    icaoCode: string;
    name: string;
    city: string;
    country: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    address: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
  };
  contact: {
    email: string;
    phone: string;
    website: string;
  };
}

export const OrganizationSetupForm: React.FC<OrganizationSetupFormProps> = ({
  ownerUid,
  onSuccess,
  onCancel,
  isOpen,
  guidance,
  stepper,
  needHelp,
}) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<'form' | 'preview'>('form');
  const [formData, setFormData] = useState<FormData>(() => ({
    name: '',
    type: 'club',
    regulationPreference: 'auto',
    currency: DEFAULT_CURRENCY,
    baseAirport: {
      icaoCode: '',
      name: '',
      city: '',
      country: '',
      coordinates: {
        latitude: 0,
        longitude: 0,
      },
      address: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
    },
    contact: {
      email: '',
      phone: '',
      website: '',
    },
  }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 3) {
        // Handle nested objects like baseAirport.address.street
        const [section, subsection, field] = parts;
        setFormData(prev => ({
          ...prev,
          [section]: {
            ...(prev[section as keyof typeof prev] as Record<string, any>),
            [subsection]: {
              ...(prev[section as keyof typeof prev] as Record<string, any>)?.[subsection],
              [field]: value,
            },
          },
        }));
      } else {
        // Handle simple nested objects like baseAirport.icaoCode
        const [section, field] = parts;
        setFormData(prev => ({
          ...prev,
          [section]: {
            ...(prev[section as keyof typeof prev] as Record<string, any>),
            [field]: value,
          },
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Generate a random 8-character join code
      const joinCode = Math.random().toString(36).substring(2, 10).toUpperCase();



      const organizationData: Organization = {
        id: crypto.randomUUID(),
        name: formData.name,
        slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
        type: formData.type,
        status: 'pending',
        joinCode,
        baseAirport: {
          icaoCode: formData.baseAirport.icaoCode,
          name: formData.baseAirport.name,
          city: formData.baseAirport.city,
          country: formData.baseAirport.country,
          coordinates: {
            latitude: formData.baseAirport.coordinates.latitude,
            longitude: formData.baseAirport.coordinates.longitude,
          },
        },
        contact: {
          email: formData.contact.email,
          phone: formData.contact.phone,
          website: formData.contact.website,
        },
        createdByUserId: ownerUid,
        ownerUid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        settings: {
          timezone: 'UTC',
          currency: formData.currency,
          regulationPreference: formData.regulationPreference,
          enableFlightSharing: true,
          enableDispatch: true,
          preflightRequirements: {
            requireWeatherCheck: true,
            requireWeightBalance: true,
            requireFlightPlan: true,
            requireMaintenanceCheck: true,
          },
          operations: {
            units: { fuel: 'liters', oil: 'liters' },
            timeCapture: { trackBlock: true, trackAirborne: true },
            maintenanceBasis: 'tacho',
            journeyLog: { requireFields: false, defaultDepartureFromBase: true },
          },
        },
        members: [ownerUid],
      };

      await onSuccess(organizationData);
    } catch (err) {
      logger.error('Error creating organization:', err);
      setError(err instanceof Error ? err.message : 'Failed to create organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFieldError = (fieldPath: string) => {
    return ''; // No field errors to return
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-4xl w-full rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
          {stepper}
          <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4">
            {step === 'form' ? t('organization.createOrganization') : t('organization.reviewOrganizationDetails')}
          </Dialog.Title>
          {guidance}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {step === 'form' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">{t('settings.basicInformation')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Organization Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter organization name"
                      required
                      error={getFieldError('name')}
                    />
                  </div>
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Organization Type
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    >
                      <option value="club">Flying Club</option>
                      <option value="school">Flying School</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="regulationPreference" className="block text-sm font-medium text-gray-700">
                      Aviation Regulation
                    </label>
                    <select
                      id="regulationPreference"
                      name="regulationPreference"
                      value={formData.regulationPreference}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    >
                      <option value="auto">Auto-detect (based on country)</option>
                      <option value="FAA">FAA (US regulations)</option>
                      <option value="EASA">EASA (EU regulations)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      This will pre-select the regulation for flight logs. Auto-detect will choose based on your base airport country.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
                      {t('settings.currency')}
                    </label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    >
                      {SUPPORTED_CURRENCIES.map((code) => (
                        <option key={code} value={code}>{CURRENCY_META[code].label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('settings.currencyDescription')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Base Airport */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">{t('settings.baseAirport')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="baseAirport.icaoCode" className="block text-sm font-medium text-gray-700">
                      {t('settings.icaoCode')}
                    </label>
                    <Input
                      id="baseAirport.icaoCode"
                      name="baseAirport.icaoCode"
                      value={formData.baseAirport.icaoCode}
                      onChange={handleInputChange}
                      placeholder="e.g., KLAX"
                      required
                      error={getFieldError('baseAirport.icaoCode')}
                    />
                  </div>
                  <div>
                    <label htmlFor="baseAirport.name" className="block text-sm font-medium text-gray-700">
                      Airport Name
                    </label>
                    <Input
                      id="baseAirport.name"
                      name="baseAirport.name"
                      value={formData.baseAirport.name}
                      onChange={handleInputChange}
                      placeholder="Enter airport name"
                      required
                      error={getFieldError('baseAirport.name')}
                    />
                  </div>
                  <div>
                    <label htmlFor="baseAirport.city" className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    <Input
                      id="baseAirport.city"
                      name="baseAirport.city"
                      value={formData.baseAirport.city}
                      onChange={handleInputChange}
                      placeholder="Enter city"
                      required
                      error={getFieldError('baseAirport.city')}
                    />
                  </div>
                  <div>
                    <label htmlFor="baseAirport.country" className="block text-sm font-medium text-gray-700">
                      Country
                    </label>
                    <Input
                      id="baseAirport.country"
                      name="baseAirport.country"
                      value={formData.baseAirport.country}
                      onChange={handleInputChange}
                      placeholder="Enter country"
                      required
                      error={getFieldError('baseAirport.country')}
                    />
                  </div>
                </div>

                {/* Base Airport Address */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-600">Airport Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="baseAirport.address.street" className="block text-sm font-medium text-gray-700">
                        Street Address
                      </label>
                      <Input
                        id="baseAirport.address.street"
                        name="baseAirport.address.street"
                        value={formData.baseAirport.address.street}
                        onChange={handleInputChange}
                        placeholder="Enter street address"
                        error={getFieldError('baseAirport.address.street')}
                      />
                    </div>
                    <div>
                      <label htmlFor="baseAirport.address.city" className="block text-sm font-medium text-gray-700">
                        City
                      </label>
                      <Input
                        id="baseAirport.address.city"
                        name="baseAirport.address.city"
                        value={formData.baseAirport.address.city}
                        onChange={handleInputChange}
                        placeholder={t('common.enterCity')}
                        error={getFieldError('baseAirport.address.city')}
                      />
                    </div>
                    <div>
                      <label htmlFor="baseAirport.address.state" className="block text-sm font-medium text-gray-700">
                        State/Province
                      </label>
                      <Input
                        id="baseAirport.address.state"
                        name="baseAirport.address.state"
                        value={formData.baseAirport.address.state}
                        onChange={handleInputChange}
                        placeholder="Enter state or province"
                        error={getFieldError('baseAirport.address.state')}
                      />
                    </div>
                    <div>
                      <label htmlFor="baseAirport.address.postalCode" className="block text-sm font-medium text-gray-700">
                        Postal Code
                      </label>
                      <Input
                        id="baseAirport.address.postalCode"
                        name="baseAirport.address.postalCode"
                        value={formData.baseAirport.address.postalCode}
                        onChange={handleInputChange}
                        placeholder="Enter postal code"
                        error={getFieldError('baseAirport.address.postalCode')}
                      />
                    </div>
                    <div>
                      <label htmlFor="baseAirport.address.country" className="block text-sm font-medium text-gray-700">
                        Country
                      </label>
                      <Input
                        id="baseAirport.address.country"
                        name="baseAirport.address.country"
                        value={formData.baseAirport.address.country}
                        onChange={handleInputChange}
                        placeholder="Enter country"
                        error={getFieldError('baseAirport.address.country')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">{t('settings.contactInformation')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact.email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <Input
                      id="contact.email"
                      name="contact.email"
                      type="email"
                      value={formData.contact.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                      required
                      error={getFieldError('contact.email')}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact.phone" className="block text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <PhoneInput
                      id="contact.phone"
                      name="contact.phone"
                      value={formData.contact.phone}
                      onChange={handleInputChange}
                      error={getFieldError('contact.phone')}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact.website" className="block text-sm font-medium text-gray-700">
                      Website
                    </label>
                    <Input
                      id="contact.website"
                      name="contact.website"
                      type="url"
                      value={formData.contact.website}
                      onChange={handleInputChange}
                      placeholder="Enter website URL"
                      error={getFieldError('contact.website')}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Continue'}
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Preview content */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">{t('settings.basicInformation')}</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Organization Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Organization Type</dt>
                    <dd className="mt-1 text-sm text-gray-900 capitalize">{formData.type}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Aviation Regulation</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formData.regulationPreference === 'auto'
                        ? 'Auto-detect (based on country)'
                        : formData.regulationPreference === 'FAA'
                          ? 'FAA (US regulations)'
                          : 'EASA (EU regulations)'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('settings.currency')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{CURRENCY_META[formData.currency].label}</dd>
                  </div>
                </dl>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">{t('settings.baseAirport')}</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('settings.icaoCode')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.icaoCode}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Airport Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">City</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.city}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Country</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.country}</dd>
                  </div>
                </dl>

                {formData.baseAirport.address.street && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-600">Airport Address</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Street Address</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.address.street}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">City</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.address.city}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">State/Province</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.address.state}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Postal Code</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.address.postalCode}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Country</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formData.baseAirport.address.country}</dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">{t('settings.contactInformation')}</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.contact.email}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.contact.phone || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Website</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.contact.website || 'N/A'}</dd>
                  </div>
                </dl>
              </div>

              <div className="flex justify-end">
                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('form')}
                    disabled={loading}
                  >
                    Back to Edit
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? t('organization.creating') : t('organization.createOrganization')}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {needHelp}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}; 